import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  CheckCircle,
  Download,
  FileText,
  Grid,
  List,
  Plus,
  Printer,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import { Button, LoadingSpinner, EmptyState, ErrorMessage } from '@/core/components';
import { usePermissions } from '@/core/hooks';
import { useInvoices, useBillingSummary } from '../hooks';
import { InvoiceCard, InvoiceSearch, BillingSummaryCard } from '../components';
import { downloadInvoicePdf, formatCurrency, printInvoicePdf } from '../utils';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import type { BillingSearchFilters, Invoice, InvoiceStatus } from '../types';

type InvoicePeriod = 'week' | 'month';

interface PayrollTimesheetEntry {
  id: string;
  caregiverId: string;
  date: string;
  hours: number;
  scheduledHours?: number;
  realHours?: number;
  varianceMinutes?: number;
  notes: string;
  source?: 'carer-app' | 'manual';
  clientName?: string;
}

const TIMESHEETS_KEY = 'admin:carer-payroll-timesheets';
const BILLING_STATUS_KEY = 'billing:invoice-status-overrides';
const AUTO_INVOICES_KEY = 'billing:auto-invoices';
const DEFAULT_BILLING_RATE = 28;

const clientBillingRates: Record<string, number> = {
  'Margaret Williams': 29.5,
  'George Taylor': 28,
  'Eileen Cooper': 30,
  'Arthur Bennett': 27.5,
  'Rose Hughes': 31,
};

const payrollRates: Record<string, number> = {
  'carer-adrian': 14.5,
  'carer-daniel': 15.25,
  'carer-amelia': 14.75,
  'carer-oliver': 16,
  'carer-grace': 15,
};

const defaultBillingTimesheets: PayrollTimesheetEntry[] = [
  {
    id: 'billing-ts-001',
    caregiverId: 'carer-adrian',
    date: '2026-05-18',
    hours: 8,
    scheduledHours: 8,
    realHours: 8.1,
    clientName: 'Margaret Williams',
    source: 'carer-app',
    notes: 'Verified personal care and medication support visit',
  },
  {
    id: 'billing-ts-002',
    caregiverId: 'carer-daniel',
    date: '2026-05-19',
    hours: 6.5,
    scheduledHours: 6.5,
    realHours: 6.25,
    clientName: 'George Taylor',
    source: 'carer-app',
    notes: 'Completed companionship and meal preparation visit',
  },
  {
    id: 'billing-ts-003',
    caregiverId: 'carer-amelia',
    date: '2026-05-20',
    hours: 7.75,
    scheduledHours: 7.5,
    realHours: 7.75,
    clientName: 'Eileen Cooper',
    source: 'carer-app',
    notes: 'Completed personal care route',
  },
  {
    id: 'billing-ts-004',
    caregiverId: 'carer-oliver',
    date: '2026-05-21',
    hours: 5.25,
    scheduledHours: 5.25,
    realHours: 5.4,
    clientName: 'Arthur Bennett',
    source: 'carer-app',
    notes: 'Completed supervision and review visit',
  },
  {
    id: 'billing-ts-005',
    caregiverId: 'carer-grace',
    date: '2026-05-22',
    hours: 6,
    scheduledHours: 6,
    realHours: 6.1,
    clientName: 'Rose Hughes',
    source: 'carer-app',
    notes: 'Completed evening care route',
  },
];

function todayIso(): string {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function toLocalIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getWeekStart(dateString: string): string {
  const date = new Date(`${dateString}T00:00:00`);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return toLocalIsoDate(date);
}

function getPeriodStart(dateString: string, period: InvoicePeriod): string {
  return period === 'week' ? getWeekStart(dateString) : `${dateString.slice(0, 7)}-01`;
}

function getPeriodEnd(dateString: string, period: InvoicePeriod): string {
  const start = new Date(`${getPeriodStart(dateString, period)}T00:00:00`);
  if (period === 'week') {
    start.setDate(start.getDate() + 6);
    return toLocalIsoDate(start);
  }
  return toLocalIsoDate(new Date(start.getFullYear(), start.getMonth() + 1, 0));
}

function dateWithinPeriod(dateString: string, selectedDate: string, period: InvoicePeriod): boolean {
  if (period === 'week') return getWeekStart(dateString) === getWeekStart(selectedDate);
  return dateString.slice(0, 7) === selectedDate.slice(0, 7);
}

function addDays(dateString: string, days: number): string {
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + days);
  return toLocalIsoDate(date);
}

function getStatus(invoice: Invoice, overrides: Record<string, InvoiceStatus>): InvoiceStatus {
  return overrides[invoice.id] ?? invoice.status;
}

function applyPaymentStatus(invoice: Invoice, status: InvoiceStatus): Invoice {
  if (status === 'PAID') {
    return { ...invoice, status, paidAmount: invoice.totalAmount, balanceDue: 0 };
  }
  if (status === 'PARTIALLY_PAID') {
    const paidAmount = Number((invoice.totalAmount * 0.5).toFixed(2));
    return { ...invoice, status, paidAmount, balanceDue: Number((invoice.totalAmount - paidAmount).toFixed(2)) };
  }
  return { ...invoice, status, paidAmount: invoice.status === 'PAID' ? invoice.totalAmount : invoice.paidAmount };
}

function buildAutoInvoices(entries: PayrollTimesheetEntry[], selectedDate: string, period: InvoicePeriod): Invoice[] {
  const periodEntries = entries.filter((entry) => dateWithinPeriod(entry.date, selectedDate, period));
  const grouped = periodEntries.reduce<Record<string, PayrollTimesheetEntry[]>>((groups, entry) => {
    const clientName = entry.clientName || 'Unassigned client';
    groups[clientName] = [...(groups[clientName] ?? []), entry];
    return groups;
  }, {});

  return Object.entries(grouped).map(([clientName, clientEntries], index) => {
    const rate = clientBillingRates[clientName] ?? DEFAULT_BILLING_RATE;
    const lineItems = clientEntries.map((entry, itemIndex) => {
      const units = entry.scheduledHours ?? entry.hours;
      const total = Number((units * rate).toFixed(2));
      return {
        id: `auto-line-${entry.id}`,
        billableItemId: entry.id,
        serviceDate: entry.date,
        serviceCode: 'HC-HOUR',
        serviceDescription: `${entry.notes || 'Completed home care visit'}${entry.realHours ? ` (real ${entry.realHours.toFixed(2)}h)` : ''}`,
        providerName: entry.caregiverId,
        unitType: 'HOUR' as const,
        units,
        unitRate: rate,
        subtotal: total,
        adjustments: 0,
        total,
        clientName,
        clientId: clientName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      };
    });
    const subtotal = Number(lineItems.reduce((sum, item) => sum + item.total, 0).toFixed(2));
    const periodStart = getPeriodStart(selectedDate, period);
    const periodEnd = getPeriodEnd(selectedDate, period);
    const status: InvoiceStatus = index === 0 ? 'SENT' : 'DRAFT';

    return {
      id: `auto-invoice-${period}-${periodStart}-${clientName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      invoiceNumber: `AUTO-${period === 'week' ? 'W' : 'M'}-${periodStart.replace(/-/g, '')}-${index + 1}`,
      invoiceType: period === 'week' ? 'WEEKLY_AUTO' : 'MONTHLY_AUTO',
      clientId: clientName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      clientName,
      payerId: 'payer-private',
      payerType: 'PRIVATE_PAY',
      payerName: 'Private Pay',
      status,
      periodStart,
      periodEnd,
      invoiceDate: todayIso(),
      dueDate: addDays(todayIso(), 14),
      lineItems,
      subtotal,
      taxAmount: 0,
      discountAmount: 0,
      adjustmentAmount: 0,
      totalAmount: subtotal,
      paidAmount: 0,
      balanceDue: subtotal,
      notes: 'Automatically generated from completed visits and verified carer check-in/check-out payroll hours.',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });
}

function calculateCarerCost(entries: PayrollTimesheetEntry[], clientName?: string): number {
  return entries
    .filter((entry) => !clientName || entry.clientName === clientName)
    .reduce((sum, entry) => sum + entry.hours * (payrollRates[entry.caregiverId] ?? 15), 0);
}

export const InvoiceList: React.FC = () => {
  const { can } = usePermissions();
  const [filters, setFilters] = useState<BillingSearchFilters>({});
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [invoicePeriod, setInvoicePeriod] = useState<InvoicePeriod>('week');
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [isGeneratingVerifiedInvoices, setIsGeneratingVerifiedInvoices] = useState(false);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, InvoiceStatus>>(() =>
    getLocalStorage(BILLING_STATUS_KEY, {})
  );
  const { data: invoiceData, isLoading, error, refetch } = useInvoices(filters);
  const { data: summary } = useBillingSummary(filters);

  const payrollEntries = getLocalStorage<PayrollTimesheetEntry[]>(TIMESHEETS_KEY, defaultBillingTimesheets);
  const autoInvoices = useMemo(
    () => buildAutoInvoices(payrollEntries, selectedDate, invoicePeriod),
    [payrollEntries, selectedDate, invoicePeriod]
  );
  useEffect(() => {
    setLocalStorage(AUTO_INVOICES_KEY, autoInvoices);
  }, [autoInvoices]);
  const existingInvoices = invoiceData?.items || [];
  const invoices = useMemo(() => {
    const merged = [...autoInvoices, ...existingInvoices].map((invoice) =>
      applyPaymentStatus(invoice, getStatus(invoice, statusOverrides))
    );
    return merged.filter((invoice, index, all) => all.findIndex((item) => item.id === invoice.id) === index);
  }, [autoInvoices, existingInvoices, statusOverrides]);
  const invoicedBillableIds = new Set(existingInvoices.flatMap((invoice) => invoice.lineItems.map((item) => item.billableItemId)));
  const unbilledVisits = payrollEntries.filter(
    (entry) => dateWithinPeriod(entry.date, selectedDate, invoicePeriod) && !invoicedBillableIds.has(entry.id)
  );
  const billableHours = unbilledVisits.reduce((sum, entry) => sum + (entry.scheduledHours ?? entry.hours), 0);
  const autoInvoiceRevenue = autoInvoices.reduce((sum, invoice) => sum + invoice.totalAmount, 0);
  const totalOutstanding = invoices.reduce((sum, invoice) => sum + invoice.balanceDue, 0);
  const overdueInvoices = invoices.filter((invoice) => invoice.status === 'PAST_DUE' || (invoice.balanceDue > 0 && new Date(invoice.dueDate) < new Date()));
  const monthlyRevenue = invoices
    .filter((invoice) => invoice.invoiceDate.slice(0, 7) === selectedDate.slice(0, 7))
    .reduce((sum, invoice) => sum + invoice.totalAmount, 0);
  const selectedPeriodEntries = payrollEntries.filter((entry) => dateWithinPeriod(entry.date, selectedDate, invoicePeriod));
  const carerCost = calculateCarerCost(selectedPeriodEntries);
  const revenueVsCarerCost = autoInvoiceRevenue - carerCost;
  const selectedPeriodLabel = invoicePeriod === 'week'
    ? `${new Date(`${getPeriodStart(selectedDate, invoicePeriod)}T00:00:00`).toLocaleDateString('en-GB')} - ${new Date(`${getPeriodEnd(selectedDate, invoicePeriod)}T00:00:00`).toLocaleDateString('en-GB')}`
    : new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(new Date(`${selectedDate.slice(0, 7)}-01T00:00:00`));
  const periodStart = getPeriodStart(selectedDate, invoicePeriod);
  const periodEnd = getPeriodEnd(selectedDate, invoicePeriod);

  const updateInvoiceStatus = (invoiceId: string, status: InvoiceStatus) => {
    const next = { ...statusOverrides, [invoiceId]: status };
    setStatusOverrides(next);
    setLocalStorage(BILLING_STATUS_KEY, next);
    toast.success(`Invoice marked as ${status.replace(/_/g, ' ').toLowerCase()}`);
  };

  const downloadPdf = (invoice: Invoice) => {
    downloadInvoicePdf(invoice);
    toast.success('Invoice PDF downloaded');
  };

  const printPdf = (invoice: Invoice) => {
    if (!printInvoicePdf(invoice)) {
      toast.error('Allow pop-ups to print the PDF');
    }
  };

  const generateVerifiedInvoices = async () => {
    setIsGeneratingVerifiedInvoices(true);
    try {
      const response = await fetch('/api/billing/generate-from-visits', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ periodStart, periodEnd }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to generate invoices');
      toast.success(`${payload.generated?.length ?? 0} invoice(s) generated from verified visits`);
      await refetch();
    } catch (generationError) {
      toast.error(generationError instanceof Error ? generationError.message : 'Failed to generate invoices');
    } finally {
      setIsGeneratingVerifiedInvoices(false);
    }
  };

  const downloadAccountingCsv = (target: 'generic' | 'quickbooks' | 'xero') => {
    window.open(`/api/accounting/export.csv?target=${target}&periodStart=${periodStart}&periodEnd=${periodEnd}`, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={(error as Error).message || 'Failed to load invoices'}
        retry={refetch}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Invoicing</h1>
          <p className="text-gray-600 mt-1">
            {invoices.length} total invoices. Automatic invoices are generated from completed visits.
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex border border-gray-300 rounded-md">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${
                viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
              aria-label="Grid view"
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${
                viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
              aria-label="List view"
            >
              <List className="h-5 w-5" />
            </button>
          </div>
          <Button
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={() => downloadAccountingCsv('quickbooks')}
          >
            QuickBooks CSV
          </Button>
          <Button
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={() => downloadAccountingCsv('xero')}
          >
            Xero CSV
          </Button>
          {can('billing:write') && (
            <Link to="/billing/new">
              <Button leftIcon={<Plus className="h-4 w-4" />}>
                New Invoice
              </Button>
            </Link>
          )}
        </div>
      </div>

      {summary && <BillingSummaryCard summary={summary} />}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Billable Hours</p>
            <FileText className="h-5 w-5 text-[#6c3a80]" />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{billableHours.toFixed(2)}</p>
          <p className="mt-1 text-xs text-slate-500">Calculated from completed carer visits in {selectedPeriodLabel}.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Unbilled Visits</p>
            <AlertTriangle className={`h-5 w-5 ${unbilledVisits.length ? 'text-amber-600' : 'text-emerald-600'}`} />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{unbilledVisits.length}</p>
          <p className="mt-1 text-xs text-slate-500">Completed visits not found in saved invoices.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Outstanding</p>
            <AlertTriangle className={`h-5 w-5 ${totalOutstanding ? 'text-red-600' : 'text-emerald-600'}`} />
          </div>
          <p className="mt-3 text-3xl font-bold text-slate-950">{formatCurrency(totalOutstanding)}</p>
          <p className="mt-1 text-xs text-slate-500">{overdueInvoices.length} overdue / past-due invoice{overdueInvoices.length === 1 ? '' : 's'}.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-slate-500">Revenue vs Carer Cost</p>
            <TrendingUp className={`h-5 w-5 ${revenueVsCarerCost >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
          </div>
          <p className={`mt-3 text-3xl font-bold ${revenueVsCarerCost >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {formatCurrency(revenueVsCarerCost)}
          </p>
          <p className="mt-1 text-xs text-slate-500">Invoice revenue minus carer payroll cost.</p>
        </div>
      </section>

      <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-emerald-700">Automatic billing from completed visits</p>
            <h2 className="mt-1 text-xl font-bold text-emerald-950">Weekly / monthly invoice generator</h2>
            <p className="mt-2 text-sm leading-6 text-emerald-900">
              The system reads verified carer visit hours, calculates billable hours, creates client invoices, detects unbilled visits and compares revenue with carer cost.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="inline-flex rounded-md border border-emerald-300 bg-white p-1">
              {(['week', 'month'] as InvoicePeriod[]).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setInvoicePeriod(item)}
                  className={`rounded px-3 py-1.5 text-sm font-semibold capitalize transition ${
                    invoicePeriod === item ? 'bg-emerald-700 text-white' : 'text-emerald-800 hover:bg-emerald-100'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className="rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm text-slate-950"
            />
            <Button variant="outline" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={() => void refetch()}>
              Refresh
            </Button>
            <Button
              leftIcon={<CheckCircle className="h-4 w-4" />}
              onClick={generateVerifiedInvoices}
              disabled={isGeneratingVerifiedInvoices}
            >
              {isGeneratingVerifiedInvoices ? 'Generating...' : 'Generate Verified Invoices'}
            </Button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-950">Generated Invoice Control</h2>
            <p className="mt-1 text-sm text-slate-600">
              Download PDF, print PDF and update payment status for automatic and existing invoices.
            </p>
          </div>
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
            Monthly revenue: {formatCurrency(monthlyRevenue)}
          </div>
        </div>
        <div className="mt-5 overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Hours</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="px-4 py-3">
                    <Link to={`/billing/${invoice.id}`} className="font-semibold text-[#5e3474] hover:underline">
                      {invoice.invoiceNumber}
                    </Link>
                    <p className="text-xs text-slate-500">{invoice.invoiceType}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{invoice.clientName || invoice.payerName}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {new Date(invoice.periodStart).toLocaleDateString('en-GB')} - {new Date(invoice.periodEnd).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {invoice.lineItems.reduce((sum, item) => sum + item.units, 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 font-bold text-slate-950">{formatCurrency(invoice.totalAmount)}</td>
                  <td className="px-4 py-3">
                    <select
                      value={invoice.status}
                      onChange={(event) => updateInvoiceStatus(invoice.id, event.target.value as InvoiceStatus)}
                      className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700"
                    >
                      {(['DRAFT', 'SENT', 'PARTIALLY_PAID', 'PAID', 'PAST_DUE', 'DISPUTED'] as InvoiceStatus[]).map((status) => (
                        <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => downloadPdf(invoice)}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Download className="h-3.5 w-3.5" />
                        Download PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => printPdf(invoice)}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Printer className="h-3.5 w-3.5" />
                        Print PDF
                      </button>
                      {invoice.status !== 'PAID' && (
                        <button
                          type="button"
                          onClick={() => updateInvoiceStatus(invoice.id, 'PAID')}
                          className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                          Paid
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <InvoiceSearch filters={filters} onFiltersChange={setFilters} />

      {invoices.length === 0 ? (
        <EmptyState
          title="No invoices found"
          description="Get started by creating your first invoice."
          action={
            can('billing:write') ? (
              <Link to="/billing/new">
                <Button leftIcon={<Plus className="h-4 w-4" />}>
                  Create Invoice
                </Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
          }
        >
          {invoices.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              compact={viewMode === 'list'}
            />
          ))}
        </div>
      )}
    </div>
  );
};
